import { Box, Step, StepLabel, Stepper, StepConnector, stepConnectorClasses, styled } from '@mui/material';
import { useLocation } from 'react-router-dom';
import React from 'react';

const ColorlibConnector = styled(StepConnector)(({ }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: '#ffc621',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: '#ffc621',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: '#e0e0e0',
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean } }>(
  ({ ownerState }) => ({
    backgroundColor: ownerState.active ? '#ffc621' : '#e0e0e0',
    zIndex: 1,
    color: '#fff',
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...(ownerState.completed && {
      backgroundColor: '#ffc621',
    }),
  })
);

function ColorlibStepIcon(props: any) {
  const { active, completed, className } = props;
  const icons: { [index: string]: string } = {
    1: '/icon/firststep.png',
    2: '/icon/secondstep.png',
    3: '/icon/thairdstep.png',
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      <img 
        src={icons[String(props.icon)]} 
        alt={`step-${props.icon}`} 
        style={{ width: 24, height: 24 }} 
      />
    </ColorlibStepIconRoot>
  );
}

const steps = ['Cart', 'Shipping', 'Confirmation'];

const ProgressBar = () => {
  const location = useLocation();

  const computeRouteStep = React.useCallback(() => {
    const pathname = location.pathname || '';
    if (pathname.startsWith('/checkout')) return 1; // Shipping step
    if (pathname.startsWith('/confirmation') || pathname.startsWith('/order-confirmation') || pathname.startsWith('/success')) return 2; // Confirmation
    return 0; // Cart
  }, [location.pathname]);

  const getOverride = () => {
    const v = sessionStorage.getItem('progressStepOverride');
    const n = v ? parseInt(v, 10) : NaN;
    return Number.isNaN(n) ? null : n;
  };

  const [activeStep, setActiveStep] = React.useState<number>(() => {
    const routeStep = computeRouteStep();
    const override = getOverride();
    return override !== null ? override : routeStep;
  });

  // Update on route change
  React.useEffect(() => {
    const routeStep = computeRouteStep();
    const override = getOverride();
    // If we are back to Cart, clear any override so progress restarts
    if (routeStep === 0 && override !== null) {
      sessionStorage.removeItem('progressStepOverride');
      setActiveStep(0);
      return;
    }
    setActiveStep(override !== null ? override : routeStep);
  }, [computeRouteStep]);

  // Listen for custom progress step change events
  React.useEffect(() => {
    const handler = () => {
      const routeStep = computeRouteStep();
      const override = getOverride();
      setActiveStep(override !== null ? override : routeStep);
    };
    window.addEventListener('progressStepChange', handler as EventListener);
    // Also clear override on hard refresh/navigation away
    const onBeforeUnload = () => {
      sessionStorage.removeItem('progressStepOverride');
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('progressStepChange', handler as EventListener);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [computeRouteStep]);

  return (
    <Box sx={{ width: '90%', maxWidth: '800px', margin: '0 auto', padding: '20px 0' }}>
      <Stepper 
        alternativeLabel 
        activeStep={activeStep} 
        connector={<ColorlibConnector />}
      >
        {steps.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepLabel 
              StepIconComponent={ColorlibStepIcon}
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '14px',
                  fontWeight: index <= activeStep ? 600 : 400,
                  color: index <= activeStep ? '#000' : 'text.secondary',
                  marginTop: '8px',
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default ProgressBar;
